import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Progress, List, Avatar, Button, Space, Tag } from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ScanOutlined,
  PlusOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

interface DashboardStats {
  totalDpps: number;
  publishedDpps: number;
  pendingValidation: number;
  recentScans: number;
  totalProducts: number;
  totalOrganisations: number;
  totalCertifications: number;
  avgCompleteness: number;
}

interface RecentActivity {
  id: string;
  type: 'dpp_created' | 'scan_completed' | 'validation_passed';
  title: string;
  description: string;
  timestamp: string;
  status: 'success' | 'pending' | 'warning';
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalDpps: 156,
    publishedDpps: 142,
    pendingValidation: 8,
    recentScans: 24,
    totalProducts: 156,
    totalOrganisations: 34,
    totalCertifications: 89,
    avgCompleteness: 87,
  });
  
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([
    {
      id: '1',
      type: 'dpp_created',
      title: 'Product erstellt',
      description: 'mpn: HV480-48P-R1 | name: 48-Pin Hochstrom-Steckverbinder HV480 | eclass: 27-27-04-08',
      timestamp: 'vor 5 Minuten',
      status: 'success',
    },
    {
      id: '2',
      type: 'scan_completed',
      title: 'gtin gescannt & gemappt',
      description: 'gtin13: 4005176893421 | Organisation.legalName: Dräxlmaier Group GmbH & Co. KG | Brand.name: DRÄXLMAIER',
      timestamp: 'vor 15 Minuten',
      status: 'success',
    },
    {
      id: '3',
      type: 'validation_passed',
      title: 'Validierung bestanden',
      description: 'mpn: A2135406803 | 5 Certification | 24 ProductPropertyValue | compliance: 92%',
      timestamp: 'vor 1 Stunde',
      status: 'success',
    },
    {
      id: '4',
      type: 'dpp_created',
      title: 'DPP in Überprüfung',
      description: 'mpn: 0203301110 | Organisation.legalName: Robert Bosch GmbH | certificationStatus: in_review',
      timestamp: 'vor 2 Stunden',
      status: 'pending',
    },
  ]);

  const getActivityIcon = (type: string, status: string) => {
    const iconProps = {
      style: { fontSize: '16px' }
    };

    switch (type) {
      case 'dpp_created':
        return <FileTextOutlined {...iconProps} style={{ ...iconProps.style, color: '#1890ff' }} />;
      case 'scan_completed':
        return <ScanOutlined {...iconProps} style={{ ...iconProps.style, color: '#52c41a' }} />;
      case 'validation_passed':
        return <CheckCircleOutlined {...iconProps} style={{ ...iconProps.style, color: '#52c41a' }} />;
      default:
        return <ClockCircleOutlined {...iconProps} style={{ ...iconProps.style, color: '#faad14' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'pending':
        return 'warning';
      case 'warning':
        return 'error';
      default:
        return 'default';
    }
  };

  const completionRate = stats.totalDpps > 0 ? (stats.publishedDpps / stats.totalDpps) * 100 : 0;
  const validationRate = stats.totalDpps > 0 ? ((stats.publishedDpps + stats.pendingValidation) / stats.totalDpps) * 100 : 0;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1>Dashboard</h1>
        <p style={{ color: '#8c8c8c', margin: 0 }}>
          Übersicht über alle Digital Product Passports und Systemaktivitäten
        </p>
      </div>

      {/* Quick Actions */}
      <Card style={{ marginBottom: 24 }} title="Schnellaktionen">
        <Space size="large">
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => navigate('/dpp-creation')}
          >
            Neuen DPP erstellen
          </Button>
          <Button 
            icon={<ScanOutlined />}
            onClick={() => navigate('/barcode-scanner')}
          >
            Barcode scannen
          </Button>
          <Button 
            icon={<EyeOutlined />}
            onClick={() => navigate('/dpp-list')}
          >
            Alle DPPs anzeigen
          </Button>
        </Space>
      </Card>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Product (total)"
              value={stats.totalDpps}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="statecode: published"
              value={stats.publishedDpps}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="statecode: in_review"
              value={stats.pendingValidation}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="gtin scans (today)"
              value={stats.recentScans}
              prefix={<ScanOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* DPP Entity Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8} lg={8}>
          <Card size="small">
            <Statistic title="Product" value={stats.totalProducts} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={8}>
          <Card size="small">
            <Statistic title="Organisation" value={stats.totalOrganisations} valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={8}>
          <Card size="small">
            <Statistic title="Certification" value={stats.totalCertifications} valueStyle={{ color: '#13c2c2' }} />
          </Card>
        </Col>
      </Row>

      {/* Progress Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={8}>
          <Card title="DPP-Vervollständigung">
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={Math.round(completionRate)}
                format={percent => `${percent}%`}
                strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
              />
              <p style={{ margin: '16px 0 0', color: '#8c8c8c' }}>
                {stats.publishedDpps} von {stats.totalDpps} DPPs vollständig
              </p>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Validierungs-Status">
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={Math.round(validationRate)}
                format={percent => `${percent}%`}
                strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
              />
              <p style={{ margin: '16px 0 0', color: '#8c8c8c' }}>
                {stats.publishedDpps + stats.pendingValidation} von {stats.totalDpps} validiert
              </p>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="DPP Schema-Abdeckung">
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={stats.avgCompleteness}
                format={percent => `${percent}%`}
                strokeColor={{ '0%': '#faad14', '100%': '#52c41a' }}
              />
              <p style={{ margin: '16px 0 0', color: '#8c8c8c' }}>
                Durchschnittliche Feld-Abdeckung
              </p>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Recent Activity */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Letzte Aktivitäten" extra={<Button type="link">Alle anzeigen</Button>}>
            <List
              dataSource={recentActivities}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar icon={getActivityIcon(item.type, item.status)} />
                    }
                    title={
                      <Space>
                        {item.title}
                        <Tag color={getStatusColor(item.status)}>
                          {item.status}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <div>{item.description}</div>
                        <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: 4 }}>
                          {item.timestamp}
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="System-Status">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>API-Status</span>
                <Tag color="success">Online</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Datenbank</span>
                <Tag color="success">Verbunden</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>LLM-Service</span>
                <Tag color="success">Aktiv</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Scanner-Service</span>
                <Tag color="success">Bereit</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>DPP Schema</span>
                <Tag color="success">v1.0</Tag>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
